import apiClient from '../apiClient';

/**
 * User Management API
 * Handles all user-related operations via backend API
 */

/**
 * Get current user profile information
 * Endpoint: GET /api/users/me
 */
export const getCurrentUserProfile = async () => {
    try {
        console.log('[UserAPI] Fetching current user profile...');
        const response = await apiClient.get('/users/me');
        console.log('[UserAPI] Current user profile fetched:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error fetching current user profile:', error);
        throw error;
    }
};

/**
 * Get all users for current tenant
 * Endpoint: GET /api/users
 */
export const getAllUsers = async (params = {}) => {
    try {
        console.log('[UserAPI] Fetching all users...', params);
        
        // Backend expects search, page, size
        const queryParams = {
            page: params.page || 0,
            size: params.size || 1000,
        };
        
        if (params.search) {
            queryParams.search = params.search;
        }

        if (params.role) {
            queryParams.role = params.role;
        }

        const response = await apiClient.get('/users', { params: queryParams });

        // If backend returns a paginated response wrapper, we return it as is
        // so the frontend can read content, totalElements, totalPages, etc.
        const data = response.data;
        if (data && data.content !== undefined) {
            return data;
        }

        // Fallback for non-paginated response structure
        const users = data || [];
        const userList = Array.isArray(users) ? users : [];

        console.log('[UserAPI] Users fetched:', userList.length);
        return {
            content: userList,
            totalElements: userList.length,
            totalPages: 1,
            number: 0
        };
    } catch (error) {
        console.error('[UserAPI] Error fetching users:', error);
        throw error;
    }
};

/**
 * Create a new user
 * Endpoint: POST /api/user
 * @param {Object} userData - User creation data
 * @param {string} userData.username - Username
 * @param {string} userData.email - Email
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} userData.password - Password
 * @param {boolean} userData.enabled - Whether user is enabled
 */
export const createUser = async (userData) => {
    try {
        console.log('[UserAPI] Creating user:', userData.username);
        const response = await apiClient.post('/user', userData);
        console.log('[UserAPI] User created:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error creating user:', error);
        throw error;
    }
};

/**
 * Update user information
 * Endpoint: PUT /api/user/{userId}
 * @param {string} userId - User ID
 * @param {Object} userData - Updated user data
 */
export const updateUser = async (userId, userData) => {
    try {
        console.log('[UserAPI] Updating user:', userId);
        const response = await apiClient.put(`/user/${userId}`, userData);
        console.log('[UserAPI] User updated:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error updating user:', error);
        throw error;
    }
};

/**
 * Enable or disable a user
 * Endpoint: PATCH /api/user/{userId}/enabled
 * @param {string} userId - User ID
 * @param {boolean} enabled - Whether to enable or disable
 */
export const setUserEnabled = async (userId, enabled) => {
    try {
        console.log(`[UserAPI] ${enabled ? 'Enabling' : 'Disabling'} user:`, userId);
        const response = await apiClient.patch(`/user/${userId}/enabled`, { enabled });
        console.log('[UserAPI] User status updated:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error updating user status:', error);
        throw error;
    }
};

/**
 * Reset a user's password
 * Endpoint: POST /api/user/{userId}/reset-password
 */
export const resetPassword = async (userId, password) => {
    try {
        console.log('[UserAPI] Resetting password for user:', userId);
        const response = await apiClient.post(`/user/${userId}/reset-password`, { password });
        console.log('[UserAPI] Password reset:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error resetting password:', error);
        throw error;
    }
};

/**
 * Assign a role to a user
 * Endpoint: POST /api/user/{userId}/roles
 * @param {string} userId - User ID
 * @param {string} roleName - Role name to assign
 */
export const assignRole = async (userId, roleName) => {
    try {
        console.log('[UserAPI] Assigning role to user:', userId, roleName);
        const response = await apiClient.post(`/user/${userId}/roles`, { roleName });
        console.log('[UserAPI] Role assigned:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error assigning role:', error);
        throw error;
    }
};

/**
 * Revoke a role from a user
 * Endpoint: DELETE /api/user/{userId}/roles/{roleName}
 * @param {string} userId - User ID
 * @param {string} roleName - Role name to revoke
 */
export const revokeRole = async (userId, roleName) => {
    try {
        console.log('[UserAPI] Revoking role from user:', userId, roleName);
        const response = await apiClient.delete(`/user/${userId}/roles/${roleName}`);
        console.log('[UserAPI] Role revoked:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error revoking role:', error);
        throw error;
    }
};

/**
 * Import multiple users
 * Endpoint: POST /api/user/import
 * @param {Array} users - Array of user objects to import
 */
export const importUsers = async (users) => {
    try {
        console.log('[UserAPI] Importing users:', users.length);
        const response = await apiClient.post('/user/import', users);
        console.log('[UserAPI] Users imported:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error importing users:', error);
        throw error;
    }
};

/**
 * Get all users with pagination and search
 * Endpoint: GET /api/users?search=&page=&size=
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term
 * @param {number} params.page - Page number (0-indexed)
 * @param {number} params.size - Page size
 */
export const getAllUsersWithPagination = async (params = {}) => {
    try {
        const { search = '', page = 0, size = 2000 } = params;
        console.log('[UserAPI] Fetching users with pagination:', { search, page, size });
        const response = await apiClient.get('/users', {
            params: { search, page, size }
        });
        console.log('[UserAPI] Paginated users fetched:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error fetching paginated users:', error);
        throw error;
    }
};

/**
 * Get available roles for tenant
 * Endpoint: GET /api/tenants/{tenantId}/roles
 * @param {string} tenantId - Tenant ID
 */
export const getRoles = async (tenantId) => {
    try {
        console.log('[UserAPI] Fetching roles for tenant:', tenantId);
        const response = await apiClient.get(`/tenants/${tenantId}/roles`);
        console.log('[UserAPI] Roles fetched:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error fetching roles:', error);
        throw error;
    }
};

/**
 * Transfer all assigned/delegated customers of a user to another user
 * Endpoint: POST /api/user/{userId}/transfer-customers
 * @param {string} userId - Source User ID
 * @param {string} toUserId - Target User ID
 */
export const transferUserCustomers = async (userId, toUserId) => {
    try {
        console.log('[UserAPI] Transferring customers from user:', userId, 'to:', toUserId);
        const response = await apiClient.post(`/user/${userId}/transfer-customers`, { toUserId });
        console.log('[UserAPI] Customers transferred successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('[UserAPI] Error transferring customers:', error);
        throw error;
    }
};
